import type { Chord, NoteEvent } from '@tunasong/music-lib'
import { chordToEvents } from '../player/chord-to-events.js'
import { useStabScheduler } from '../player/stab-scheduler.js'

const TEMPO_BPM = 100
const BEAT_MS = 60_000 / TEMPO_BPM // 600 ms at 100 BPM
const VAMP_CHORD_MS = BEAT_MS
const VAMP_CHORDS_PER_ITERATION = 4

/** Total length of one vamp loop iteration — 2.4 s at 100 BPM, 4 quarter notes. */
export const VAMP_ITERATION_MS = VAMP_CHORDS_PER_ITERATION * VAMP_CHORD_MS

export interface VampStab {
  events: NoteEvent[]
  /** Offset (in ms) from the start of one vamp loop iteration. */
  delayMs: number
}

/**
 * Build the stab sequence for one vamp iteration. Each stab is one chord at
 * 100 BPM quarter-note spacing. Pure helper.
 */
export const buildVampStabs = (vamp: Chord[]): VampStab[] =>
  vamp.map((chord, index) => ({
    events: chordToEvents(chord, '4n'),
    delayMs: index * VAMP_CHORD_MS,
  }))

export interface UseModeLabPlaybackProps {
  vamp: Chord[]
  /** When true, the vamp loops indefinitely until `stop()` is called. */
  loop: boolean
}

export interface UseModeLabPlaybackResult {
  play: () => void
  stop: () => void
  isPlaying: boolean
}

/**
 * Looping vamp playback for Mode Lab. Thin wrapper over `useStabScheduler` —
 * see `frontend/music-ui/src/player/stab-scheduler.ts` for scheduling semantics.
 * The vamp is re-read from props on each iteration so prop changes between
 * loops take effect on the next iteration boundary.
 */
export const useModeLabPlayback = (props: UseModeLabPlaybackProps): UseModeLabPlaybackResult => {
  const { vamp, loop } = props
  return useStabScheduler({
    buildStabs: () => buildVampStabs(vamp),
    loopIterationMs: loop ? VAMP_ITERATION_MS : undefined,
  })
}
